import { useEffect } from "react";
import type { Rec, Finding } from "@shared/types.js";
import { fdate } from "../lib/format.js";
import { RULE_TITLE, sevPill } from "../lib/rules.js";
import { derivedStatus } from "../lib/exportData.js";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="dr-row">
      <span className="dr-k">{label}</span>
      <span className="dr-v">{value === null || value === undefined || value === "" ? <span className="muted">&ndash;</span> : value}</span>
    </div>
  );
}

/** Everything the Masterfile holds about one record, plus its findings. */
export function RecordDrawer({
  rec, findings, onClose,
}: {
  rec: Rec; findings: Finding[]; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mine = findings.filter((f) => f.uid === rec.uid);

  return (
    <div className="drawer-back" onClick={onClose} role="presentation">
      <aside
        className="drawer" role="dialog" aria-modal="true"
        aria-label={rec.name ?? "Record detail"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhead">
          <div style={{ minWidth: 0 }}>
            <h3 style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {rec.name ?? "(no name)"}
            </h3>
            <div className="muted" style={{ fontSize: 11 }}>
              {rec.compLabel} &middot; {rec.sheet} row {rec.excelRow}
            </div>
          </div>
          <button className="btn btn--sm" type="button" onClick={onClose} style={{ marginLeft: "auto" }}>
            Close
          </button>
        </div>

        <div className="drawer-body">
          <div className="dr-status">
            <span className={`pill ${rec.nrDone ? "p-done" : rec.estDone ? "p-est" : rec.received ? "p-await" : "p-none"}`}>
              {derivedStatus(rec)}
            </span>
          </div>

          <h4 className="dr-h">Identity</h4>
          <Row label="Code" value={rec.code} />
          <Row label="Product" value={rec.product} />
          <Row label="Town" value={rec.town} />
          <Row label="Region" value={rec.region} />
          <Row label="Category" value={rec.cat ? <span className="catbadge">{rec.cat}</span> : null} />

          <h4 className="dr-h">Contract rate</h4>
          <Row label="CR status" value={rec.recvRaw} />
          <Row label="Received" value={rec.received ? "Yes" : "No"} />
          <Row label="Received date" value={fdate(rec.recvDate)} />

          <h4 className="dr-h">New rate</h4>
          <Row label="Status" value={rec.nrRaw} />
          <Row label="Input date" value={fdate(rec.nrDate)} />
          <Row label="PIC" value={rec.nrPic} />

          <h4 className="dr-h">Estimation</h4>
          <Row label="Status" value={rec.estRaw} />
          <Row label="Input date" value={fdate(rec.estDate)} />
          <Row label="PIC" value={rec.estPic} />

          <h4 className="dr-h">Downstream</h4>
          <Row label="Rate type" value={rec.rateType} />
          <Row label="Selling decision" value={rec.selling} />
          <Row label="Crosschecked by" value={rec.ccBy} />
          <Row label="Crosscheck date" value={fdate(rec.ccDate)} />
          <Row label="Upload date" value={fdate(rec.upDate)} />
          <Row label="Export date" value={fdate(rec.exDate)} />
          <Row label="Cancellation policy" value={rec.cancel} />

          <h4 className="dr-h">
            Findings {mine.length > 0 && <span className="muted">({mine.length})</span>}
          </h4>
          {mine.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No data-quality issues on this record.</div>
          ) : (
            mine.map((f, i) => (
              <div className={`finding sev-${f.severity}`} key={`${f.rule}-${i}`} style={{ padding: "9px 0" }}>
                <span className="stripe" />
                <div>
                  <div className="who">
                    {RULE_TITLE[f.rule] ?? f.rule}{" "}
                    <span className={`pill ${sevPill(f.severity)}`}>{f.severity}</span>
                  </div>
                  <div className="msg">{f.message}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}
